
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useOrganizationClubs } from '@/hooks/useOrganizationClubs';
import { useSeasons } from '@/hooks/useSeasons';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { teamEditSchema, TeamEditFormData } from './teamEditSchema';

interface TeamEditFormProps {
  initialData: TeamEditFormData;
  isSubmitting: boolean;
  onSubmit: (data: TeamEditFormData) => void;
  onCancel: () => void;
  organizationId?: string;
}

export const TeamEditForm = ({
  initialData,
  isSubmitting,
  onSubmit,
  onCancel,
  organizationId
}: TeamEditFormProps) => {
  const form = useForm<TeamEditFormData>({
    resolver: zodResolver(teamEditSchema),
    defaultValues: initialData,
  });

  const selectedClubId = form.watch('club_id');
  const selectedSeasonId = form.watch('season_id');

  const { clubs } = useOrganizationClubs(organizationId);
  const { seasons } = useSeasons(selectedClubId);

  // Reset form when initialData changes (e.g., when dialog opens with new team data)
  useEffect(() => {
    form.reset(initialData);
  }, [initialData, form]);

  const { data: divisions } = useQuery({
    queryKey: ['divisions', selectedSeasonId],
    queryFn: async () => {
      if (!selectedSeasonId) return [];
      
      const { data, error } = await supabase
        .from('divisions')
        .select('*')
        .eq('season_id', selectedSeasonId)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedSeasonId,
  });

  const handleSubmit = (data: TeamEditFormData) => {
    // Convert 'no-division' back to undefined for the API
    const processedData = {
      ...data,
      division_id: data.division_id === 'no-division' ? undefined : data.division_id
    };
    onSubmit(processedData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="club_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Club</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a club" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {clubs.map((club) => (
                    <SelectItem key={club.id} value={club.id}>
                      {club.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="season_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Season</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a season" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season.id} value={season.id}>
                      {season.season_year} - {season.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="division_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Division (Optional)</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || "no-division"}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={selectedSeasonId ? "Select a division" : "Select a season first"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="no-division">No division assigned</SelectItem>
                  {divisions?.map((division) => (
                    <SelectItem key={division.id} value={division.id}>
                      {division.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Team Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter team name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <Label>Birth Date Cutoff (Optional)</Label>
          <div className="flex space-x-2">
            <FormField
              control={form.control}
              name="birth_month"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input 
                      placeholder="MM" 
                      maxLength={2}
                      {...field} 
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <span className="flex items-center">/</span>
            <FormField
              control={form.control}
              name="birth_day"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input 
                      placeholder="DD" 
                      maxLength={2}
                      {...field} 
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 31)) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <span className="flex items-center">/</span>
            <FormField
              control={form.control}
              name="birth_year"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input 
                      placeholder="YYYY" 
                      maxLength={4}
                      {...field} 
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        const currentYear = new Date().getFullYear();
                        if (value === '' || (parseInt(value) >= 1900 && parseInt(value) <= currentYear + 10)) {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Players must be born on or after this date to be eligible for this team
          </p>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Update'}
          </Button>
        </div>
      </form>
    </Form>
  );
};
